import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lock, History, LogOut, Trash2, Edit3, Repeat, X, Star, ChevronDown, ChevronUp, KeyRound, ShieldCheck, Sparkles } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useToast } from "@/components/Toasts";
import { useSheetHistory } from "@/hooks/useSheetHistory";
import { API, useAuth } from "@/auth/AuthProvider";
import { useAuthSheet } from "@/auth/AuthSheetProvider";
import { useTenant } from "@/tenant/TenantProvider";
import Avatar from "@/components/Avatar";
import { FuellyLogo } from "@/components/FuellyBrand";
import { PLATFORM } from "@/data/platform";
import { FieldPrivacyHint } from "@/components/PrivacyNote";
import { ApiError, fetchJson } from "@/lib/api";
import { EVENTS, track } from "@/lib/analytics";
import { formatPrice } from "@/lib/format";
import { saveErrorMessage } from "@/lib/serverMessages";
import { localeTag } from "@/i18n";
import { PASSWORD, PRIVACY } from "@/constants/testIds";
import AllergyPicker from "@/components/AllergyPicker";
import TagPicker from "@/components/TagPicker";
import { parseAllergyTokens } from "@/data/allergens";
import { EXCLUDE_INGREDIENTS } from "@/data/excludeIngredients";

// Tabs ordered so that Orders sits right before Settings (per user request).
// The id is the state machine's; only the label moves with the language.
const TABS = [
  { id: "info", labelKey: "profile.tabInfo" },
  { id: "goals", labelKey: "profile.tabGoals" },
  { id: "taste", labelKey: "profile.tabTaste" },
  { id: "restaurants", labelKey: "profile.tabPlaces" },
  { id: "orders", labelKey: "profile.tabOrders" },
  { id: "settings", labelKey: "profile.tabSettings" },
];

const InfoRow = ({ icon, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-8 h-8 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
      {icon}
    </div>
    <p className="text-[13px] leading-relaxed text-neutral-700">{children}</p>
  </div>
);

/** Dates follow the interface language; prices never do (see lib/format.js). */
const formatDateTime = (iso) =>
  new Date(iso).toLocaleString(localeTag(), {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(localeTag(), { day: "numeric", month: "short" });

const Profile = ({ onBack, onReorder }) => {
  const { user, refreshUser, logout, deleteAccount } = useAuth();
  const { openSignIn } = useAuthSheet();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [tab, setTab] = useState("info");
  const [editing, setEditing] = useState(false);
  const [orders, setOrders] = useState(null);
  const [restaurants, setRestaurants] = useState(null);
  // Remount key for the restaurants tab. Nothing ever bumps it today, so the
  // remount it exists for cannot fire; kept as-is rather than quietly changing
  // what renders, but the setter is gone because nothing called it.
  const [restaurantsRefreshKey] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  useSheetHistory(infoOpen, () => setInfoOpen(false));

  // The strip scrolls, so a tab tapped at its edge pulls itself into view.
  useEffect(() => {
    document
      .getElementById(`tab-${tab}`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [tab]);

  const loadOrders = () =>
    fetch(`${API}/orders`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setOrders)
      .catch(() => setOrders([]));

  const loadRestaurants = () =>
    fetch(`${API}/restaurants`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setRestaurants)
      .catch(() => setRestaurants([]));

  // Info tab needs favorites too
  useEffect(() => {
    if (tab === "orders" && orders === null) loadOrders();
    if (tab === "restaurants" && restaurants === null) loadRestaurants();
  }, [tab, orders, restaurants]);

  const toggleFavorite = async (orderId) => {
    try {
      const res = await fetch(`${API}/orders/${orderId}/favorite`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) return;
      const { is_favorite } = await res.json();
      // Update in place rather than refetching: the server already told us the
      // new value, and clearing the list would flash a spinner over the tap.
      setOrders((current) =>
        (current || []).map((o) => (o.order_id === orderId ? { ...o, is_favorite } : o))
      );
    } catch (e) {
      // ignore — the star simply does not move
    }
  };

  // Reachable straight after a sign-out, or from a stale link: offer the way back
  // in rather than a blank screen.
  if (!user) return <SignedOutProfile onBack={onBack} onSignIn={openSignIn} />;

  return (
    <div className="pb-20">
      {/* One header, everything up top: arrow and title on the left, the "i"
          and who-is-signed-in in the right corner. No Fuelly here — the footer
          already says whose platform this is, and no second identity block
          below — a profile that introduces its owner twice reads as two pages.
          pt-8 and the 44px row height mirror the path chooser's header, so the
          avatar sits in the same corner spot on both screens instead of
          hopping when the guest taps it. */}
      {/* Header and tabs travel together and stick, like every list screen's
          header does — scrolled deep into Orders, the way to another section
          is still one tap up, not a scroll away. */}
      <div className="sticky top-0 z-20 bg-[#F9F9F7]/95 backdrop-blur">
      <div className="px-5 pt-8 pb-4 min-h-[44px] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 shrink-0">
          <button
            data-testid="profile-back"
            onClick={onBack}
            className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0"
            aria-label={t("common.back")}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-lg font-black tracking-tight text-neutral-900">
            {t("profile.title")}
          </h1>
        </div>
        {/* The corner holds the avatar alone — the same spot it occupies on the
            path chooser, so tapping it never makes it hop. Name and email left
            the corner: the fields card below already states both, and the "i"
            went with them — the Fuelly card's "read what it does" opens the
            same sheet. */}
        <Avatar name={user.name} email={user.email} picture={user.picture} size={40} testId="profile-avatar" />
      </div>

      {/* Underlined tabs, deliberately NOT the menu's chips: in the menu a
          pill means "jump/filter the list", here it means "switch section" —
          one look per behaviour, or the look stops meaning anything. */}
      <div className="overflow-x-auto no-scrollbar border-b border-neutral-200">
        <div className="flex gap-1 px-5 min-w-max">
          {TABS.map((tabItem) => (
            <button
              key={tabItem.id}
              id={`tab-${tabItem.id}`}
              data-testid={`tab-${tabItem.id}`}
              onClick={() => setTab(tabItem.id)}
              className={`px-3 py-2.5 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${
                tab === tabItem.id
                  ? "border-neutral-900 text-neutral-900"
                  : "border-transparent text-neutral-500"
              }`}
            >
              {t(tabItem.labelKey)}
            </button>
          ))}
        </div>
      </div>
      </div>

      <div className="px-5 pt-5">
        {tab === "info" && <InfoTab user={user} onEdit={() => setEditing(true)} />}
        {tab === "goals" && <GoalsTab user={user} refreshUser={refreshUser} />}
        {tab === "orders" && (
          <OrdersTab orders={orders} onReorder={onReorder} onToggleFav={toggleFavorite} />
        )}
        {tab === "taste" && <TasteTab user={user} refreshUser={refreshUser} />}
        {tab === "restaurants" && (
          <RestaurantsTab
            key={restaurantsRefreshKey}
            restaurants={restaurants}
            onReorder={onReorder}
            onToggleFav={toggleFavorite}
          />
        )}
        {tab === "settings" && (
          <SettingsTab
            hasPassword={!!user.has_password}
            onChangePassword={() => setChangingPassword(true)}
            onLogout={async () => {
              await logout();
              toast(t("toast.signedOut"));
              onBack();
            }}
            onDelete={() => setConfirmDelete(true)}
          />
        )}
      </div>

      {/* The page's Fuelly moment: instead of a bare "powered by", one short
          sentence on what the profile buys, with the full story one tap away. */}
      <div className="mx-5 mt-8 bg-white border border-neutral-100 rounded-2xl p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-neutral-100 flex items-center justify-center shrink-0">
          <FuellyLogo size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-neutral-900">{t("profile.footerTitle")}</div>
          <p className="text-xs text-neutral-500 leading-relaxed mt-0.5">
            {t("profile.footerBody")}
          </p>
          <button
            data-testid="profile-footer-more"
            onClick={() => setInfoOpen(true)}
            className="mt-2 text-xs font-bold text-[#E34A27] underline underline-offset-2"
          >
            {t("profile.footerMore")}
          </button>
        </div>
      </div>

      {/* What a profile buys — the same bottom sheet as the language and
          allergy pickers. Every line is checkable against the code: the AI
          reads the profile on each request, allergies are excluded in code,
          orders drive one-tap reorder, and visibility follows PrivacyNote. */}
      {infoOpen && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center"
          onClick={() => setInfoOpen(false)}
        >
          <div
            data-testid="profile-info-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t("profile.infoTitle")}
            className="w-full sm:max-w-md max-h-[80vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl p-4 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 px-2 py-3">
              {t("profile.infoTitle")}
            </div>
            <div className="px-2 space-y-4">
              <InfoRow icon={<Sparkles size={16} className="text-[#E34A27]" />}>
                {t("profile.infoAi")}
              </InfoRow>
              <InfoRow icon={<ShieldCheck size={16} className="text-[#22C55E]" />}>
                {t("profile.infoAllergies")}
              </InfoRow>
              <InfoRow icon={<History size={16} className="text-neutral-500" />}>
                {t("profile.infoOrders")}
              </InfoRow>
              <InfoRow icon={<Lock size={16} className="text-neutral-500" />}>
                {t("profile.infoPrivacy")}
              </InfoRow>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <EditProfileModal
          user={user}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refreshUser();
          }}
        />
      )}

      {changingPassword && (
        <PasswordModal
          hasPassword={!!user.has_password}
          onClose={() => setChangingPassword(false)}
          onSaved={refreshUser}
        />
      )}

      {confirmDelete && (
        <ConfirmDeleteModal
          onClose={() => setConfirmDelete(false)}
          onConfirm={async () => {
            await deleteAccount();
            onBack();
          }}
        />
      )}
    </div>
  );
};

const SignedOutProfile = ({ onBack, onSignIn }) => {
  const { t } = useTranslation();
  return (
    <div className="px-5 pt-6 pb-20 min-h-screen">
      <button
        data-testid="profile-back"
        onClick={onBack}
        className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center"
        aria-label={t("common.back")}
      >
        <ArrowLeft size={18} />
      </button>
      <div className="mt-10 text-center">
        <div className="w-16 h-16 rounded-full bg-neutral-900 text-white flex items-center justify-center mx-auto mb-4">
          <FuellyLogo size={26} />
        </div>
        <h1 className="text-2xl font-black tracking-tight text-neutral-900">
          {t("profile.signedOutTitle")}
        </h1>
        <p className="text-sm text-neutral-500 mt-2 max-w-[34ch] mx-auto">
          {t("profile.signedOutBody", { platform: PLATFORM.name })}
        </p>
        <button
          data-testid="profile-signin-btn"
          onClick={onSignIn}
          className="mt-6 w-full min-h-14 py-2 px-4 rounded-2xl bg-[#E34A27] text-white font-bold text-base tracking-wide active:scale-[0.98] transition-transform"
        >
          {t("auth.signIn")}
        </button>
      </div>
    </div>
  );
};

// ─── Tabs ──────────────────────────────────────────────────────────────────

const InfoTab = ({ user, onEdit }) => {
  const { t } = useTranslation();
  // The stored value is "male" | "female" | "other". An older profile could hold
  // something else, so an unknown value is shown as the server has it rather
  // than as a missing key.
  const genderLabel = (value) => {
    if (!value) return "—";
    const key = `profile.gender${value.charAt(0).toUpperCase()}${value.slice(1)}`;
    return t(key, { defaultValue: value });
  };
  return (
    <div>
      {/* amp-block: a name, an email address and the body figures behind them.
          None of it is ours to record, so this panel is left out of session
          replay entirely rather than masked — see lib/analytics.js. */}
      <div className="bg-white border border-neutral-100 rounded-2xl p-4 space-y-3 amp-block">
        <Row label={t("common.name")} value={user.name || "—"} />
        {/* The email is the login, so it is stated but not editable — changing
            it would need a verification step this stack does not have. */}
        <Row
          label={t("profile.email")}
          value={
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <span className="truncate">{user.email}</span>
              <Lock size={12} className="shrink-0 text-neutral-400" />
            </span>
          }
        />
        <Row label={t("profile.gender")} value={genderLabel(user.gender)} />
        <Row label={t("profile.age")} value={user.age ? `${user.age}` : "—"} />
        <Row
          label={t("profile.height")}
          value={user.height_cm ? t("profile.heightValue", { value: user.height_cm }) : "—"}
        />
        <Row
          label={t("profile.weight")}
          value={user.weight_kg ? t("profile.weightValue", { value: user.weight_kg }) : "—"}
        />
      </div>
      <button
        data-testid="edit-profile-btn"
        onClick={onEdit}
        className="mt-4 w-full min-h-12 py-2 px-3 rounded-2xl bg-neutral-900 text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
      >
        <Edit3 size={16} className="shrink-0" /> {t("profile.edit")}
      </button>
      <p className="text-[11px] text-neutral-400 mt-2 px-1">{t("profile.emailNote")}</p>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-neutral-500 font-medium">{label}</span>
    <span className="font-bold text-neutral-900 text-right">{value}</span>
  </div>
);

const GoalsTab = ({ user, refreshUser }) => {
  const { t } = useTranslation();
  const [val, setVal] = useState(user.goal || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ goal: val }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new ApiError(res.status, j.detail);
      }
      setSaved(true);
      // The goal itself travels: it is a category the guest picked from a list,
      // it shapes every plate the model builds, and it is the one profile field
      // a funnel is worth splitting by.
      track(EVENTS.PROFILE_SAVED, { section: "goals", has_goal: Boolean(val) });
      await refreshUser();
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setErr(e);
    } finally {
      setSaving(false);
    }
  };
  // Ready-made goals, so nobody stares at an empty box wondering what a goal
  // is supposed to sound like. A tap fills the field; the field stays the
  // truth, so editing or writing your own is the same gesture as before.
  const presetsRaw = t("profile.goalPresets", { returnObjects: true });
  const presets = Array.isArray(presetsRaw) ? presetsRaw : [];
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 px-1">
        {t("profile.goalLabel")}
      </div>
      {presets.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {presets.map((preset, i) => (
            <button
              key={preset}
              data-testid={`goal-preset-${i}`}
              onClick={() => setVal(preset)}
              className={`h-9 px-3.5 rounded-full text-[13px] font-bold transition-colors ${
                val.trim() === preset
                  ? "bg-neutral-900 text-white"
                  : "bg-white border border-neutral-200 text-neutral-700"
              }`}
            >
              {preset}
            </button>
          ))}
        </div>
      )}
      <textarea
        data-testid="goal-textarea"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        rows={4}
        placeholder={t("profile.goalPlaceholder")}
        className="w-full bg-white border-2 border-neutral-200 rounded-3xl p-4 text-base text-neutral-900 outline-none focus:border-neutral-900 transition-colors resize-none"
      />
      <p className="text-[11px] text-neutral-400 mt-2 px-1">{t("profile.goalHint")}</p>
      {err && (
        <div data-testid="goal-error" className="mt-3 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          {saveErrorMessage(t, err)}
        </div>
      )}
      <button
        data-testid="goal-save-btn"
        onClick={save}
        disabled={saving}
        className="mt-4 w-full min-h-12 py-2 px-3 rounded-2xl bg-[#E34A27] text-white font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-50"
      >
        {saving ? t("common.saving") : saved ? t("common.saved") : t("profile.goalSave")}
      </button>
    </div>
  );
};

const OrdersTab = ({ orders, onReorder, onToggleFav }) => {
  const { t } = useTranslation();
  if (orders === null) return <Skeleton />;
  if (orders.length === 0) {
    return <Empty title={t("profile.ordersEmptyTitle")} subtitle={t("profile.ordersEmptyBody")} />;
  }
  // Sort: favorites first (keeping date desc within each group)
  const sorted = [...orders].sort((a, b) => {
    const af = a.is_favorite ? 1 : 0;
    const bf = b.is_favorite ? 1 : 0;
    if (af !== bf) return bf - af;
    return new Date(b.placed_at) - new Date(a.placed_at);
  });
  const anyFav = sorted.some((o) => o.is_favorite);
  return (
    <div className="space-y-3">
      {anyFav && (
        <div className="flex items-center gap-1.5 px-1 mb-1">
          <Star size={12} className="text-[#F59E0B] fill-[#F59E0B] shrink-0" />
          <span className="text-[10px] font-black uppercase tracking-wider text-neutral-500">
            {t("profile.favoritesHint")}
          </span>
        </div>
      )}
      {sorted.map((o) => (
        <OrderCard key={o.order_id} order={o} onReorder={onReorder} onToggleFav={onToggleFav} />
      ))}
    </div>
  );
};

const OrderCard = ({ order: o, onReorder, onToggleFav }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const isFav = !!o.is_favorite;
  // The order remembers who composed it; the history never said so. A guest who
  // cannot tell which meal the AI built has no reason to ask it for the next one.
  const builtByAi = o.items.some((it) => it.source === "ai");
  const visibleItems = expanded ? o.items : o.items.slice(0, 3);
  const hiddenCount = o.items.length - visibleItems.length;
  return (
    <div
      data-testid={`order-${o.order_id}`}
      className={`rounded-2xl p-4 transition-colors ${
        isFav
          ? "bg-gradient-to-b from-[#FFC266]/10 to-transparent border-2 border-[#F59E0B]/40"
          : "bg-white border border-neutral-100"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1 mb-1 empty:hidden">
            {isFav && (
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[9px] font-black uppercase tracking-wider text-[#B45309]">
                <Star size={9} className="fill-[#F59E0B] shrink-0" /> {t("profile.favorite")}
              </div>
            )}
            {builtByAi && (
              <div
                data-testid={`order-ai-badge-${o.order_id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E34A27]/10 text-[9px] font-black uppercase tracking-wider text-[#E34A27]"
              >
                <Sparkles size={9} className="shrink-0" /> {t("profile.builtByAi")}
              </div>
            )}
          </div>
          <div className="font-black text-neutral-900 text-sm">{o.restaurant_name}</div>
          <div className="text-[11px] text-neutral-500 mt-0.5">{formatDateTime(o.placed_at)}</div>
        </div>
        <button
          data-testid={`star-${o.order_id}`}
          onClick={() => onToggleFav(o.order_id)}
          className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
            isFav ? "bg-[#FFC266]/25" : "bg-neutral-50"
          }`}
          aria-label={isFav ? t("profile.removeFavorite") : t("profile.addFavorite")}
        >
          <Star size={16} className={isFav ? "text-[#F59E0B] fill-[#F59E0B]" : "text-neutral-400"} />
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <div className="text-[10px] text-neutral-500 uppercase tracking-wider">
          {t("common.items", { count: o.items.length })}
        </div>
        <div className="text-base font-black text-neutral-900 shrink-0">{formatPrice(o.grand)}</div>
      </div>
      <ul className="mt-3 space-y-0.5">
        {visibleItems.map((it, i) => {
          const optNote = [it.options?.doneness, it.options?.style, it.options?.note].filter(Boolean).join(" · ");
          return (
            <li key={i} className="text-[13px] text-neutral-700 flex items-start gap-2">
              <span className="w-1 h-1 rounded-full bg-neutral-400 mt-2 shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="font-semibold">{it.name}</span>{" "}
                <span className="text-neutral-400">×{it.qty}</span>
                {optNote && <span className="text-neutral-500 italic"> · {optNote}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      {hiddenCount > 0 && (
        <button
          data-testid={`expand-${o.order_id}`}
          onClick={() => setExpanded(true)}
          className="mt-2 text-[11px] font-bold text-neutral-500 hover:text-neutral-900 flex items-center gap-1 text-left"
        >
          <ChevronDown size={12} className="shrink-0" />{" "}
          {t("profile.showAll", { count: o.items.length })}
        </button>
      )}
      {expanded && o.items.length > 3 && (
        <button
          data-testid={`collapse-${o.order_id}`}
          onClick={() => setExpanded(false)}
          className="mt-2 text-[11px] font-bold text-neutral-500 hover:text-neutral-900 flex items-center gap-1 text-left"
        >
          <ChevronUp size={12} className="shrink-0" /> {t("profile.showLess")}
        </button>
      )}
      {onReorder && (
        <button
          data-testid={`reorder-${o.order_id}`}
          onClick={() => onReorder(o)}
          className={`mt-3 w-full min-h-11 py-2 px-3 rounded-xl font-bold text-xs flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${
            isFav ? "bg-[#E34A27] text-white" : "bg-neutral-900 text-white"
          }`}
        >
          <Repeat size={13} className="shrink-0" /> {t("profile.reorder")}
        </button>
      )}
    </div>
  );
};

const TasteTab = ({ user, refreshUser }) => {
  const { t } = useTranslation();
  // Both lists edit the same way the allergy field does: tap, pick, done.
  // Favorites offer the sitting restaurant's menu (a canonical name is what
  // the AI can match); excludes offer common ingredients whose canonical
  // English value is what the backend's matcher runs against dish names —
  // a guest reading Russian still excludes dishes from an English menu.
  // Free entries stay possible in both: the profile spans restaurants.
  const { dishes } = useTenant();
  const dishOptions = useMemo(
    () => dishes.map((d) => ({ key: d.id, value: d.name, label: d.name })),
    [dishes]
  );
  const excludeOptions = useMemo(
    () =>
      EXCLUDE_INGREDIENTS.map((e) => ({
        key: e.key,
        value: e.value,
        label: t(`excludeIngredients.${e.key}`),
      })),
    [t]
  );
  const [favs, setFavs] = useState(user.favorite_dishes || []);
  const [allergies, setAllergies] = useState(user.allergies || []);
  const [excludes, setExcludes] = useState(user.exclude_ingredients || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          favorite_dishes: favs,
          allergies,
          exclude_ingredients: excludes,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new ApiError(res.status, j.detail);
      }
      setSaved(true);
      // Counts, never the lists themselves: what somebody is allergic to and
      // what they will not eat are theirs, and the plate builder is the only
      // place either one is needed.
      track(EVENTS.PROFILE_SAVED, {
        section: "taste",
        favorites: favs.length,
        allergies_declared: allergies.length,
        excludes_declared: excludes.length,
      });
      await refreshUser();
      setTimeout(() => setSaved(false), 1800);
    } catch (e) {
      setErr(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    // amp-block: the allergies and excluded ingredients live in these pickers.
    <div className="space-y-5 amp-block">
      <TagPicker
        label={t("profile.favoriteDishes")}
        testIdPrefix="fav"
        values={favs}
        onChange={setFavs}
        options={dishOptions}
        searchPlaceholder={t("profile.favoriteDishesPlaceholder")}
      />
      {/* The same picker and the same catalog as the welcome screen. Two inputs
          with two vocabularies meant one person could hold two different
          allergies in two places, and only one of them reached the AI. The
          picker labels itself — a second heading here read as two fields. */}
      <AllergyPicker
        value={allergies.join(", ")}
        onChange={(value) => setAllergies(parseAllergyTokens(value))}
      />
      <FieldPrivacyHint testId={PRIVACY.allergies}>{t("privacy.allergiesHint")}</FieldPrivacyHint>
      <TagPicker
        label={t("profile.excludeIngredients")}
        testIdPrefix="exclude"
        values={excludes}
        onChange={setExcludes}
        options={excludeOptions}
        searchPlaceholder={t("profile.excludePlaceholder")}
      />
      <p className="text-[11px] text-neutral-400">{t("profile.tasteHint")}</p>
      {err && (
        <div data-testid="taste-error" className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
          {saveErrorMessage(t, err)}
        </div>
      )}
      <button
        data-testid="taste-save-btn"
        onClick={save}
        disabled={saving}
        className="w-full min-h-12 py-2 px-3 rounded-2xl bg-[#E34A27] text-white font-bold text-sm active:scale-[0.98] transition-transform disabled:opacity-50"
      >
        {saving ? t("common.saving") : saved ? t("common.saved") : t("profile.tasteSave")}
      </button>
    </div>
  );
};

const RestaurantsTab = ({ restaurants, onReorder, onToggleFav }) => {
  const { t } = useTranslation();
  const [expandedId, setExpandedId] = useState(null);
  const [restaurantFavs, setRestaurantFavs] = useState({}); // { restaurant_id: [orders] }

  if (restaurants === null) return <Skeleton />;

  const toggle = async (rid) => {
    if (expandedId === rid) {
      setExpandedId(null);
      return;
    }
    setExpandedId(rid);
    if (!restaurantFavs[rid]) {
      try {
        const res = await fetch(`${API}/orders?favorites=true&restaurant_id=${rid}`, {
          credentials: "include",
        });
        const data = res.ok ? await res.json() : [];
        setRestaurantFavs((prev) => ({ ...prev, [rid]: data }));
      } catch (e) {
        setRestaurantFavs((prev) => ({ ...prev, [rid]: [] }));
      }
    }
  };

  return (
    <div className="space-y-3">
      {restaurants.map((r) => {
        const isOpen = expandedId === r.restaurant_id;
        const favs = restaurantFavs[r.restaurant_id];
        return (
          <div
            key={r.restaurant_id}
            data-testid={`restaurant-${r.restaurant_id}`}
            className="bg-white border border-neutral-100 rounded-2xl overflow-hidden"
          >
            <button
              data-testid={`restaurant-toggle-${r.restaurant_id}`}
              onClick={() => toggle(r.restaurant_id)}
              className="w-full p-4 flex items-center justify-between gap-3 text-left active:bg-neutral-50 transition-colors"
            >
              <div className="min-w-0">
                <div className="font-black text-neutral-900">{r.restaurant_name}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  {t("profile.ordersCount", { count: r.orders_count })}
                  {r.last_visit && ` · ${t("profile.lastVisit", { date: formatDate(r.last_visit) })}`}
                </div>
              </div>
              {isOpen ? (
                <ChevronUp size={16} className="text-neutral-400 shrink-0" />
              ) : (
                <ChevronDown size={16} className="text-neutral-400 shrink-0" />
              )}
            </button>
            {isOpen && (
              <div className="border-t border-neutral-100 p-4 pt-3 bg-neutral-50/50">
                <div className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-2 flex items-center gap-1.5">
                  <Star size={11} className="text-[#F59E0B] fill-[#F59E0B] shrink-0" />{" "}
                  {t("profile.favoritesHere")}
                </div>
                {favs === undefined ? (
                  <div className="text-xs text-neutral-400">{t("common.loading")}</div>
                ) : favs.length === 0 ? (
                  <div className="text-xs text-neutral-500 italic">
                    {t("profile.noFavoritesHere")}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {favs.map((o) => (
                      <OrderCard key={o.order_id} order={o} onReorder={onReorder} onToggleFav={onToggleFav} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <p className="text-[11px] text-neutral-400 pt-2">
        {t("profile.futureRestaurants", { platform: PLATFORM.name })}
      </p>
    </div>
  );
};

const SettingsTab = ({ hasPassword, onChangePassword, onLogout, onDelete }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      {/* The switcher's settings home: the profile's foot deliberately has no
          global language pill (this row is where a signed-in guest expects it),
          and every other screen keeps the one in the footer. */}
      <div className="w-full min-h-14 py-2 bg-white border border-neutral-200 rounded-2xl flex items-center justify-between gap-3 px-4">
        <span className="text-sm font-bold text-neutral-900">{t("language.label")}</span>
        <LanguageSwitcher />
      </div>
      <button
        data-testid={PASSWORD.openButton}
        onClick={onChangePassword}
        className="w-full min-h-14 py-2 bg-white border border-neutral-200 rounded-2xl flex items-center gap-3 px-4 text-left active:scale-[0.99] transition-transform"
      >
        <KeyRound size={18} className="text-neutral-500 shrink-0" />
        <span className="text-sm font-bold text-neutral-900">
          {hasPassword ? t("profile.changePassword") : t("profile.setPassword")}
        </span>
        {!hasPassword && (
          <span className="ml-auto text-[10px] font-black uppercase tracking-wider text-[#E34A27] text-right">
            {t("profile.recommended")}
          </span>
        )}
      </button>
      <button
        data-testid="logout-btn"
        onClick={onLogout}
        className="w-full min-h-14 py-2 bg-white border border-neutral-200 rounded-2xl flex items-center gap-3 px-4 text-left active:scale-[0.99] transition-transform"
      >
        <LogOut size={18} className="text-neutral-500 shrink-0" />
        <span className="text-sm font-bold text-neutral-900">{t("profile.signOut")}</span>
      </button>
      <button
        data-testid="delete-account-btn"
        onClick={onDelete}
        className="w-full min-h-14 py-2 bg-white border border-red-200 rounded-2xl flex items-center gap-3 px-4 text-left active:scale-[0.99] transition-transform"
      >
        <Trash2 size={18} className="text-red-500 shrink-0" />
        <span className="text-sm font-bold text-red-600">{t("profile.deleteAccount")}</span>
      </button>
      <p className="text-[11px] text-neutral-400 pt-3 px-1">{t("profile.deleteNote")}</p>
    </div>
  );
};

// ─── Modals ─────────────────────────────────────────────────────────────────

const EditProfileModal = ({ user, onClose, onSaved }) => {
  useSheetHistory(true, onClose);
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: user.name || "",
    gender: user.gender || "",
    age: user.age || "",
    height_cm: user.height_cm || "",
    weight_kg: user.weight_kg || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload = {
        name: form.name || null,
        gender: form.gender || null,
        age: form.age ? parseInt(form.age, 10) : null,
        height_cm: form.height_cm ? parseInt(form.height_cm, 10) : null,
        weight_kg: form.weight_kg ? parseFloat(form.weight_kg) : null,
      };
      const res = await fetch(`${API}/profile`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new ApiError(res.status, j.detail);
      }
      // Which optional fields somebody was willing to fill in — not what they
      // put in them. Age, height and weight are the guest's, and the plate
      // builder is the only thing that needs them.
      track(EVENTS.PROFILE_SAVED, {
        section: "info",
        fields_filled: Object.values(payload).filter(Boolean).length,
      });
      onSaved();
    } catch (e) {
      setErr(e);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      data-testid="edit-profile-modal"
      // amp-block: the sheet where a name, an age and a weight are typed in.
      className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center amp-block"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-[#F9F9F7] rounded-t-[28px] sm:rounded-3xl p-6 pb-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="edit-close"
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center"
          aria-label={t("common.close")}
        >
          <X size={18} />
        </button>
        <h3 className="text-2xl font-black tracking-tight text-neutral-900 mb-5 pr-10">
          {t("profile.edit")}
        </h3>
        <div className="space-y-3">
          <Field label={t("common.name")} testId="edit-name" value={form.name} onChange={(v) => set("name", v)} />
          {/* Stated and locked, not omitted: a form that silently lacks the
              email reads as an oversight, a locked one as a decision. */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 px-1">
              {t("profile.email")}
            </div>
            <div className="relative">
              <input
                data-testid="edit-email"
                value={user.email}
                disabled
                className="w-full h-14 bg-neutral-100 border border-neutral-200 rounded-2xl pl-4 pr-11 text-base font-medium text-neutral-500"
              />
              <Lock size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400" />
            </div>
            <p className="text-[11px] text-neutral-400 mt-1 px-1">{t("profile.emailSupportNote")}</p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 px-1">
              {t("profile.gender")}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "male", key: "profile.genderMale" },
                { value: "female", key: "profile.genderFemale" },
                { value: "other", key: "profile.genderOther" },
              ].map((g) => (
                <button
                  key={g.value}
                  data-testid={`edit-gender-${g.value}`}
                  onClick={() => set("gender", g.value)}
                  className={`min-h-11 py-2 px-1 rounded-xl text-sm font-bold ${
                    form.gender === g.value ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-700"
                  }`}
                >
                  {t(g.key)}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label={t("profile.age")} testId="edit-age" value={form.age} onChange={(v) => set("age", v)} inputMode="numeric" />
            <Field label={t("profile.heightField")} testId="edit-height" value={form.height_cm} onChange={(v) => set("height_cm", v)} inputMode="numeric" />
            <Field label={t("profile.weightField")} testId="edit-weight" value={form.weight_kg} onChange={(v) => set("weight_kg", v)} inputMode="decimal" />
          </div>
          <FieldPrivacyHint testId={PRIVACY.bodyFields}>{t("privacy.bodyFieldsHint")}</FieldPrivacyHint>
        </div>
        <button
          data-testid="edit-save-btn"
          disabled={saving}
          onClick={save}
          className="w-full min-h-14 py-2 px-4 mt-6 rounded-2xl bg-[#E34A27] text-white font-bold text-base tracking-wide active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          {saving ? t("common.saving") : t("profile.saveChanges")}
        </button>
        {err && (
          <div data-testid="edit-error" className="mt-3 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-center">
            {saveErrorMessage(t, err)}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, testId, value, onChange, inputMode }) => (
  <div>
    <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block px-1">{label}</label>
    <input
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      inputMode={inputMode}
      className="w-full h-12 bg-white border border-neutral-200 rounded-xl px-3 text-sm font-medium text-neutral-900 outline-none focus:border-neutral-900"
    />
  </div>
);

/**
 * Set or change the account password.
 *
 * An account created through Google has no password yet (`has_password` is
 * false); for it the current-password field would be unanswerable, so it is not
 * shown and the server does not ask for one.
 */
const MIN_PASSWORD = 8; // mirrors the server's own minimum

const PasswordModal = ({ hasPassword, onClose, onSaved }) => {
  useSheetHistory(true, onClose);
  const { t } = useTranslation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);

  const save = async () => {
    if (next.length < MIN_PASSWORD) {
      setErr(t("errors.auth.passwordTooShort", { count: MIN_PASSWORD }));
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await fetchJson("/auth/password", {
        method: "PUT",
        body: JSON.stringify({
          current_password: hasPassword ? current : null,
          new_password: next,
        }),
      });
      setDone(true);
      await onSaved();
      setTimeout(onClose, 1200);
    } catch (e) {
      // A string `detail` is the server naming the problem — a wrong current
      // password, most often — and is more use than a generic apology.
      setErr(
        typeof e.detail === "string" && e.detail
          ? e.detail
          : t("errors.auth.passwordUpdateFailed")
      );
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={PASSWORD.sheet}
      className="fixed inset-0 z-[92] bg-black/50 flex items-end sm:items-center justify-center animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md bg-[#F9F9F7] rounded-t-[28px] sm:rounded-3xl p-6 pb-8 relative animate-in slide-in-from-bottom-8 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid={PASSWORD.close}
          onClick={onClose}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center"
          aria-label={t("common.close")}
        >
          <X size={18} />
        </button>
        <h3 className="text-2xl font-black tracking-tight text-neutral-900 mb-1 pr-10">
          {hasPassword ? t("profile.changePassword") : t("profile.setPassword")}
        </h3>
        <p className="text-sm text-neutral-600 mb-5">
          {hasPassword
            ? t("profile.passwordChangeSubtitle")
            : t("profile.passwordSetSubtitle")}
        </p>
        <div className="space-y-3">
          {hasPassword && (
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block px-1">
                {t("profile.currentPassword")}
              </label>
              <input
                data-testid={PASSWORD.currentInput}
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                className="w-full h-12 bg-white border border-neutral-200 rounded-xl px-3 text-sm font-medium text-neutral-900 outline-none focus:border-neutral-900"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5 block px-1">
              {t("profile.newPassword")}
            </label>
            <input
              data-testid={PASSWORD.newInput}
              type="password"
              autoComplete="new-password"
              placeholder={t("auth.passwordPlaceholder", { count: MIN_PASSWORD })}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full h-12 bg-white border border-neutral-200 rounded-xl px-3 text-sm font-medium text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-neutral-900"
            />
          </div>
        </div>
        {err && (
          <div
            data-testid={PASSWORD.error}
            className="mt-3 text-xs font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2"
          >
            {err}
          </div>
        )}
        {done && (
          <div
            data-testid={PASSWORD.success}
            className="mt-3 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2"
          >
            {t("profile.passwordUpdated")}
          </div>
        )}
        <button
          data-testid={PASSWORD.submitButton}
          onClick={save}
          disabled={saving || done || !next || (hasPassword && !current)}
          className="w-full min-h-14 py-2 px-4 mt-5 rounded-2xl bg-[#E34A27] text-white font-bold text-base tracking-wide active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          {saving
            ? t("common.saving")
            : done
            ? t("common.saved")
            : hasPassword
            ? t("profile.changePassword")
            : t("profile.setPasswordAction")}
        </button>
      </div>
    </div>
  );
};

const ConfirmDeleteModal = ({ onClose, onConfirm }) => {
  useSheetHistory(true, onClose);
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-xl font-black text-neutral-900">{t("profile.deleteTitle")}</h3>
        <p className="text-sm text-neutral-500 mt-2">{t("profile.deleteBody")}</p>
        <div className="mt-5 flex gap-2">
          <button
            data-testid="delete-cancel"
            onClick={onClose}
            className="flex-1 min-h-12 py-2 px-2 rounded-xl bg-neutral-100 text-neutral-800 font-bold text-sm"
          >
            {t("common.cancel")}
          </button>
          <button
            data-testid="delete-confirm"
            onClick={onConfirm}
            className="flex-1 min-h-12 py-2 px-2 rounded-xl bg-red-600 text-white font-bold text-sm"
          >
            {t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

const Skeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="h-20 rounded-2xl bg-neutral-100 animate-pulse" />
    ))}
  </div>
);

const Empty = ({ title, subtitle }) => (
  <div className="py-12 text-center">
    <div className="text-4xl mb-3 opacity-30">📭</div>
    <div className="font-black text-neutral-900">{title}</div>
    <div className="text-sm text-neutral-500 mt-1">{subtitle}</div>
  </div>
);

export default Profile;
